import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { api, unwrap, type Schemas } from '../api/client.js';
import { PageTitle } from '../components/Layouts.js';
import { formatPrice, useLocale } from '../components/site.js';
import { Button, Card, LinkButton, LoadError, Loading, Notice } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDateTime } from '../i18n.js';
import { useMe } from '../session.js';

type Status = Schemas['ServiceRequest']['status'];

const STATUS_STYLE: Record<Status, string> = {
  new: 'bg-blue-100 text-blue-950',
  contacted: 'bg-amber-100 text-amber-950',
  quoted: 'bg-amber-100 text-amber-950',
  won: 'bg-green-100 text-green-900',
  lost: 'bg-slate-200 text-slate-900',
  cancelled: 'bg-slate-200 text-slate-900',
};

export function RequestStatus({ status }: { status: Status }) {
  const { t } = useTranslation();
  return (
    <span
      className={`inline-block rounded-full px-3 py-1 text-sm font-semibold ${STATUS_STYLE[status]}`}
    >
      {t(`requests.status.${status}`)}
    </span>
  );
}

/** "Meine Anfragen" (US-05): my service requests and their status. */
export function Requests() {
  const { t, i18n } = useTranslation();
  const locale = useLocale();
  const timeZone = useMe().data?.account.timezone;
  const client = useQueryClient();
  const list = useQuery({
    queryKey: ['requests', locale],
    queryFn: () => unwrap(api.GET('/service-requests', { params: { query: { locale } } })),
  });
  const cancel = useMutation({
    mutationFn: (requestId: string) =>
      unwrap(api.POST('/service-requests/{requestId}/cancel', { params: { path: { requestId } } })),
    onSuccess: () => client.invalidateQueries({ queryKey: ['requests'] }),
  });

  return (
    <>
      <PageTitle>{t('requests.title')}</PageTitle>
      <p className="-mt-3 mb-4 text-slate-700">{t('requests.intro')}</p>
      {list.isPending ? <Loading /> : null}
      {list.isError ? (
        <LoadError message={errorMessage(list.error, t)} onRetry={() => void list.refetch()} />
      ) : null}
      {cancel.isError ? <Notice kind="error">{errorMessage(cancel.error, t)}</Notice> : null}
      {list.data && list.data.items.length === 0 ? (
        <Notice kind="info">{t('requests.empty')}</Notice>
      ) : null}
      {list.data && list.data.items.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {list.data.items.map((r) => (
            <li key={r.id}>
              <Card>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold">
                      {r.serviceName ?? t('request.general')}
                    </h2>
                    <p className="text-sm text-slate-700">
                      {[r.siteName, formatDateTime(r.createdAt, i18n.language, timeZone)]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  <RequestStatus status={r.status} />
                </div>
                <p className="mt-2 text-slate-800">
                  {r.payment && r.status !== 'cancelled'
                    ? t(r.payment.paidAt ? 'order.state.paid' : `order.state.${r.payment.method}`)
                    : t(`requests.explain.${r.status}`)}
                </p>
                {r.quotedPriceCents !== null ? (
                  <p className="mt-1 font-semibold">
                    {t('requests.quote', { price: formatPrice(r.quotedPriceCents, i18n.language) })}
                  </p>
                ) : null}
                {r.message ? (
                  <p className="mt-2 whitespace-pre-line break-words text-sm text-slate-700">
                    {r.message}
                  </p>
                ) : null}
                {r.payment && !r.payment.paidAt && r.status === 'quoted' ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {/* Orders from before T-96: the invoice can still be paid or withdrawn. */}
                    {r.payment.invoiceId ? (
                      <LinkButton to={`/billing/invoices/${r.payment.invoiceId}`} variant="soft">
                        {t('order.showInvoice')}
                      </LinkButton>
                    ) : null}
                    <Button
                      variant="secondary"
                      busy={cancel.isPending && cancel.variables === r.id}
                      onClick={() => cancel.mutate(r.id)}
                    >
                      {t('order.cancel')}
                    </Button>
                  </div>
                ) : null}
                {r.status === 'new' ? (
                  <Button
                    variant="secondary"
                    className="mt-3"
                    busy={cancel.isPending && cancel.variables === r.id}
                    onClick={() => cancel.mutate(r.id)}
                  >
                    {t('requests.cancel')}
                  </Button>
                ) : null}
              </Card>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}
